import { jwtDecode } from 'jwt-decode';
import { getStoredToken } from './tokenStore';

interface DecodedToken {
  exp?: number;
  name?: string;
  email?: string;
  preferred_username?: string;
  upn?: string;
  unique_name?: string;
  roles?: string[];
}

export interface UserData {
  displayName?: string;
  email?: string;
  roles?: string[];
}

function safeDecode(token: string): DecodedToken | null {
  if (!token) return null;
  try {
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
}

/**
 * Treat a token as expired shortly before it really is, so a request never
 * leaves the browser with a token that lapses while it is in flight.
 */
const EXPIRY_LEEWAY_MS = 60_000;

export function isTokenExpired(token: string | null | undefined): boolean {
  if (!token) return true;
  const decodedToken = safeDecode(token);
  return !(decodedToken?.exp && Date.now() + EXPIRY_LEEWAY_MS <= decodedToken.exp * 1000);
}

function getEmailOrPreferredUsername(decodedToken: DecodedToken | null): string | undefined {
  const candidates = [
    decodedToken?.email,
    decodedToken?.preferred_username,
    decodedToken?.upn,
    decodedToken?.unique_name,
    decodedToken?.name,
  ];
  return candidates.find(value => typeof value === 'string' && value.trim() !== '');
}

export function getUserData(): UserData {
  const token = getStoredToken();
  if (!isTokenExpired(token)) {
    const decodedToken = safeDecode(token);
    return {
      displayName: decodedToken?.name,
      email: getEmailOrPreferredUsername(decodedToken),
      roles: decodedToken?.roles,
    };
  }
  return {};
}
